"use client";

import React, { useState } from "react";
import { ShieldCheck, FileText, ArrowRight, CircleCheck, Layers, Info, Microscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { siteImage } from "@/lib/siteImages";
interface LocalizedItem {
  en: string;
  zh: string;
  es: string;
  de: string;
  fr: string;
  pt: string;
}
interface AlloySpec {
  id: string;
  grade: string;
  unsNumber: string;
  enDesignation: string;
  corrosionRating: "Standard" | "High" | "Superior" | "Extreme";
  corrosionRatingI18n: LocalizedItem;
  keyFeatures: LocalizedItem[];
  primaryApplications: LocalizedItem;
  composition: {
    element: string;
    percentage: string;
  }[];
}
interface AlloyCategory {
  id: string;
  label: LocalizedItem;
  description: LocalizedItem;
  alloys: AlloySpec[];
}
const ALLOY_CATEGORIES: AlloyCategory[] = [{
  id: "austenitic",
  label: {
    en: "Austenitic Stainless",
    zh: "奥氏体不锈钢",
    es: "Acero inoxidable austenítico",
    de: "Austenitischer Edelstahl",
    fr: "Acier inoxydable austénitique",
    pt: "Aço inoxidável austenítico"
  },
  description: {
    en: "Standard industrial grades providing balanced corrosion resistance, weldability, and mechanical strength for general filtration.",
    zh: "通用工业标准牌号，具备均衡的耐腐蚀性、焊接性能与机械强度，适用于常规工业过滤。",
    es: "Grados industriales estándar que ofrecen un equilibrio entre resistencia a la corrosión, soldabilidad y resistencia mecánica para filtración general.",
    de: "Industrielle Standardgüten mit ausgewogener Korrosionsbeständigkeit, Schweißbarkeit und mechanischer Festigkeit für allgemeine Filtrationsaufgaben.",
    fr: "Nuances industrielles standard offrant un compromis équilibré entre résistance à la corrosion, soudabilité et résistance mécanique pour la filtration générale.",
    pt: "Graus industriais padrão que oferecem resistência equilibrada à corrosão, soldabilidade e resistência mecânica para filtração geral."
  },
  alloys: [{
    id: "ss-304l",
    grade: "AISI 304L",
    unsNumber: "S30403",
    enDesignation: "1.4306 / 1.4307",
    corrosionRating: "Standard",
    corrosionRatingI18n: {
      en: "Standard",
      zh: "标准",
      es: "Estándar",
      de: "Standard",
      fr: "Standard",
      pt: "Padrão"
    },
    keyFeatures: [{
      en: "Low carbon content prevents intergranular carbide precipitation",
      zh: "超低碳含量有效防止焊接热影响区的晶间碳化物析出",
      es: "El bajo contenido de carbono previene la precipitación intergranular de carburos",
      de: "Niedriger Kohlenstoffgehalt verhindert interkristalline Karbidausscheidungen",
      fr: "Faible teneur en carbone évitant la précipitation intergranulaire de carbures",
      pt: "Baixo teor de carbono evita a precipitação intergranular de carbetos"
    }, {
      en: "Excellent formability and fine wire drawability",
      zh: "优异的深冲成形性与超细金属丝拉拔适应性",
      es: "Excelente conformabilidad y aptitud para el trefilado de alambre fino",
      de: "Ausgezeichnete Verformbarkeit und Ziehbarkeit zu Feindraht",
      fr: "Excellente formabilité et aptitude au tréfilage de fil fin",
      pt: "Excelente conformabilidade e aptidão para trefilação de arames finos"
    }, {
      en: "Economical benchmark for non-acidic liquid & gas filtration",
      zh: "非强酸性液体与气体介质过滤的高性价比工业基准",
      es: "Referencia económica para filtración de líquidos y gases no ácidos",
      de: "Wirtschaftlicher Standard für die Filtration nicht-saurer Flüssigkeiten und Gase",
      fr: "Référence économique pour la filtration de liquides et gaz non acides",
      pt: "Referência econômica para filtração de líquidos e gases não ácidos"
    }],
    primaryApplications: {
      en: "Food & beverage processing, oil straining, architectural mesh, general water treatment",
      zh: "食品与饮料加工、燃油滤清粗滤、建筑装饰丝网、通用工业水处理",
      es: "Procesamiento de alimentos y bebidas, colado de aceite, mallas arquitectónicas, tratamiento general de agua",
      de: "Lebensmittel- und Getränkeverarbeitung, Ölfiltration, Architektur-Drahtgewebe, allgemeine Wasseraufbereitung",
      fr: "Agroalimentaire, préfiltration d'huiles, toiles architecturales, traitement général de l'eau",
      pt: "Processamento de alimentos e bebidas, filtragem de óleos, telas arquitetônicas, tratamento geral de água"
    },
    composition: [{
      element: "Cr",
      percentage: "18.0 - 20.0%"
    }, {
      element: "Ni",
      percentage: "8.0 - 12.0%"
    }, {
      element: "C",
      percentage: "≤ 0.03%"
    }, {
      element: "Fe",
      percentage: "Balance"
    }]
  }, {
    id: "ss-316l",
    grade: "AISI 316L",
    unsNumber: "S31603",
    enDesignation: "1.4404",
    corrosionRating: "High",
    corrosionRatingI18n: {
      en: "High",
      zh: "高",
      es: "Alta",
      de: "Hoch",
      fr: "Élevée",
      pt: "Alta"
    },
    keyFeatures: [{
      en: "Molybdenum addition enhances resistance to pitting & crevice corrosion",
      zh: "钼元素的添加显著增强抗氯离子点蚀与缝隙腐蚀性能",
      es: "La adición de molibdeno mejora la resistencia a la corrosión por picaduras y rendijas",
      de: "Molybdänzusatz verbessert die Beständigkeit gegen Lochfraß und Spaltkorrosion",
      fr: "L'ajout de molybdène renforce la résistance à la corrosion par piqûres et caverneuse",
      pt: "A adição de molibdênio aumenta a resistência à corrosão por pites e por frestas"
    }, {
      en: "Superior performance in chloride and marine environments",
      zh: "在氯化物溶液、盐雾及海洋应用环境中具备出色耐久度",
      es: "Rendimiento superior en ambientes con cloruros y marinos",
      de: "Überlegene Leistung in chloridhaltigen und maritimen Umgebungen",
      fr: "Performances supérieures en milieux chlorurés et marins",
      pt: "Desempenho superior em ambientes com cloretos e marinhos"
    }, {
      en: "Standard alloy for high-precision sintered and woven filter elements",
      zh: "精密编织过滤网与多层真空烧结滤芯的标准通用合金",
      es: "Aleación estándar para elementos filtrantes sinterizados y tejidos de alta precisión",
      de: "Standardlegierung für hochpräzise gesinterte und gewebte Filterelemente",
      fr: "Alliage standard pour éléments filtrants tissés et frittés de haute précision",
      pt: "Liga padrão para elementos filtrantes sinterizados e tecidos de alta precisão"
    }],
    primaryApplications: {
      en: "Pharmaceuticals, marine filtration, chemical process equipment, resin strainers",
      zh: "制药无菌原料药过滤、船用海水过滤、化工工艺装备、树脂捕捉拦截器",
      es: "Farmacéutica, filtración marina, equipos de procesos químicos, retención de resinas",
      de: "Pharmazeutische Produktion, Schifffahrt/Meerwasserfiltration, chemische Prozessanlagen, Harzfänger",
      fr: "Industrie pharmaceutique, filtration marine, équipements chimiques, pièges à résine",
      pt: "Farmacêutica, filtração marinha, equipamentos de processo químico, retenção de resinas"
    },
    composition: [{
      element: "Cr",
      percentage: "16.0 - 18.0%"
    }, {
      element: "Ni",
      percentage: "10.0 - 14.0%"
    }, {
      element: "Mo",
      percentage: "2.0 - 3.0%"
    }, {
      element: "C",
      percentage: "≤ 0.03%"
    }, {
      element: "Fe",
      percentage: "Balance"
    }]
  }, {
    id: "ss-310s",
    grade: "AISI 310S",
    unsNumber: "S31008",
    enDesignation: "1.4845",
    corrosionRating: "High",
    corrosionRatingI18n: {
      en: "High",
      zh: "高",
      es: "Alta",
      de: "Hoch",
      fr: "Élevée",
      pt: "Alta"
    },
    keyFeatures: [{
      en: "High chromium and nickel content for extreme oxidation resistance",
      zh: "高铬与高镍配比，提供极端高温工况下的抗高温氧化与结焦能力",
      es: "Alto contenido de cromo y níquel para una resistencia extrema a la oxidación",
      de: "Hoher Chrom- und Nickelgehalt für extreme Oxidationsbeständigkeit",
      fr: "Forte teneur en chrome et nickel assurant une résistance extrême à l'oxydation",
      pt: "Alto teor de cromo e níquel para extrema resistência à oxidação"
    }, {
      en: "Resists carburization and thermal cycling degradation",
      zh: "耐受高温渗碳介质并减缓频繁热循环引起的疲劳剥落",
      es: "Resiste la carburación y la degradación por ciclos térmicos",
      de: "Beständig gegen Aufkohlung und thermische Wechselbeanspruchung",
      fr: "Résiste à la carburation et à la dégradation due aux cycles thermiques",
      pt: "Resiste à carburação e à degradação por ciclos térmicos"
    }, {
      en: "Maintains mechanical integrity under continuous high temperature",
      zh: "在持续高温连续运行环境中保持良好的结构强度与抗蠕变性",
      es: "Mantiene la integridad mecánica bajo altas temperaturas continuas",
      de: "Behält mechanische Festigkeit bei dauerhaft hohen Temperaturen bei",
      fr: "Conserve son intégrité mécanique en régime continu à haute température",
      pt: "Mantém a integridade mecânica sob altas temperaturas contínuas"
    }],
    primaryApplications: {
      en: "Kiln gas filtration, thermal processing, catalyst recovery, hot gas clean-up",
      zh: "工业窑炉尾气过滤、热处理炉膛气氛净化、贵金属催化剂截留、高温烟气除尘",
      es: "Filtración de gases de hornos, tratamiento térmico, recuperación de catalizadores, limpieza de gases calientes",
      de: "Ofengasfiltration, thermische Verfahrenstechnik, Katalysatorrückgewinnung, Heißgasreinigung",
      fr: "Filtration des gaz de fours, traitements thermiques, récupération de catalyseurs, dépoussiérage de gaz chauds",
      pt: "Filtração de gases de fornos, processamento térmico, recuperação de catalisadores, limpeza de gases quentes"
    },
    composition: [{
      element: "Cr",
      percentage: "24.0 - 26.0%"
    }, {
      element: "Ni",
      percentage: "19.0 - 22.0%"
    }, {
      element: "Si",
      percentage: "≤ 1.50%"
    }, {
      element: "C",
      percentage: "≤ 0.08%"
    }, {
      element: "Fe",
      percentage: "Balance"
    }]
  }]
}, {
  id: "super-austenitic",
  label: {
    en: "Super Austenitic & Duplex",
    zh: "超级奥氏体与双相钢",
    es: "Superausteníticos y dúplex",
    de: "Superaustenitisch & Duplex",
    fr: "Superausténitique et duplex",
    pt: "Superausteníticos e duplex"
  },
  description: {
    en: "Engineered for aggressive acid streams, elevated halide concentrations, and high-pressure stress-corrosion environments.",
    zh: "专为强酸介质、高浓度卤化物及高压应力腐蚀开裂工况定制的工程级合金。",
    es: "Diseñados para corrientes ácidas agresivas, altas concentraciones de haluros y entornos de corrosión bajo tensión y alta presión.",
    de: "Entwickelt für aggressive Säureströme, hohe Halogenidkonzentrationen und Hochdruck-Spannungsrisskorrosionsumgebungen.",
    fr: "Conçus pour les flux acides agressifs, les fortes concentrations d'halogénures et les milieux sous contrainte et haute pression.",
    pt: "Projetados para correntes ácidas agressivas, altas concentrações de haletos e ambientes de corrosão sob tensão em alta pressão."
  },
  alloys: [{
    id: "ss-904l",
    grade: "AISI 904L",
    unsNumber: "N08904",
    enDesignation: "1.4539",
    corrosionRating: "Superior",
    corrosionRatingI18n: {
      en: "Superior",
      zh: "卓越",
      es: "Superior",
      de: "Überlegen",
      fr: "Supérieure",
      pt: "Superior"
    },
    keyFeatures: [{
      en: "Added copper delivers exceptional resistance to sulfuric acid solutions",
      zh: "添加 1.0–2.0% 铜元素，对不同浓度硫酸溶液展现出极强的耐腐蚀能力",
      es: "El cobre añadido proporciona una resistencia excepcional a soluciones de ácido sulfúrico",
      de: "Kupferzusatz verleiht außergewöhnliche Beständigkeit gegen Schwefelsäurelösungen",
      fr: "L'ajout de cuivre confère une résistance exceptionnelle aux solutions d'acide sulfurique",
      pt: "A adição de cobre proporciona resistência excepcional a soluções de ácido sulfúrico"
    }, {
      en: "High nickel and molybdenum content prevents stress corrosion cracking",
      zh: "高含量的镍与钼有效抵御苛刻工况下的氯离子应力腐蚀开裂",
      es: "El alto contenido de níquel y molibdeno previene el agrietamiento por corrosión bajo tensión",
      de: "Hoher Nickel- und Molybdängehalt verhindert Spannungsrisskorrosion",
      fr: "La haute teneur en nickel et molybdène prévient la fissuration par corrosion sous contrainte",
      pt: "O alto teor de níquel e molibdênio previne a corrosão sob tensão"
    }, {
      en: "Ideal for concentrated organic and inorganic acid filtration",
      zh: "浓缩有机酸、无机酸及强还原性酸洗工艺过滤介质的理想之选",
      es: "Ideal para la filtración de ácidos orgánicos e inorgánicos concentrados",
      de: "Ideal für die Filtration konzentrierter organischer und anorganischer Säuren",
      fr: "Idéal pour la filtration d'acides organiques et inorganiques concentrés",
      pt: "Ideal para filtração de ácidos orgânicos e inorgânicos concentrados"
    }],
    primaryApplications: {
      en: "Phosphate fertilizer manufacturing, chemical wash screens, bleaching equipment",
      zh: "磷肥化工浓酸过滤、酸洗洗涤转鼓滤网、造纸氯漂洗涤过滤设备",
      es: "Fabricación de fertilizantes fosfatados, tamices de lavado químico, equipos de blanqueo",
      de: "Phosphatdüngerherstellung, chemische Waschsiebe, Bleichanlagen",
      fr: "Production d'engrais phosphatés, tamis de lavage chimique, unités de blanchiment",
      pt: "Produção de fertilizantes fosfatados, peneiras de lavagem química, equipamentos de branqueamento"
    },
    composition: [{
      element: "Cr",
      percentage: "19.0 - 23.0%"
    }, {
      element: "Ni",
      percentage: "23.0 - 28.0%"
    }, {
      element: "Mo",
      percentage: "4.0 - 5.0%"
    }, {
      element: "Cu",
      percentage: "1.0 - 2.0%"
    }, {
      element: "Fe",
      percentage: "Balance"
    }]
  }, {
    id: "duplex-2205",
    grade: "Duplex 2205",
    unsNumber: "S31803 / S32205",
    enDesignation: "1.4462",
    corrosionRating: "Superior",
    corrosionRatingI18n: {
      en: "Superior",
      zh: "卓越",
      es: "Superior",
      de: "Überlegen",
      fr: "Supérieure",
      pt: "Superior"
    },
    keyFeatures: [{
      en: "Two-phase ferrite/austenite microstructure doubles mechanical yield strength",
      zh: "铁素体与奥氏体两相双联金相结构，屈服强度达到传统奥氏体不锈钢的两倍",
      es: "Microestructura bifásica ferrita/austenita que duplica el límite elástico mecánico",
      de: "Zweiphasiges Ferrit/Austenit-Gefüge verdoppelt die mechanische Dehngrenze",
      fr: "Microstructure biphasée ferrite/austénite doublant la limite d'élasticité mécanique",
      pt: "Microestrutura bifásica ferrita/austenita dobra o limite de escoamento mecânico"
    }, {
      en: "High resistance to chloride stress corrosion cracking and erosion-corrosion",
      zh: "高度抵御氯化物应力腐蚀开裂，并具备出色的抗冲刷腐蚀与抗泥沙磨蚀性能",
      es: "Alta resistencia al agrietamiento por corrosión bajo tensión por cloruros y a la erosión-corrosión",
      de: "Hohe Beständigkeit gegen chloridinduzierte Spannungsrisskorrosion und Erosionskorrosion",
      fr: "Forte résistance à la corrosion sous contrainte chlorurée et à l'érosion-corrosion",
      pt: "Alta resistência à corrosão sob tensão induzida por cloretos e à erosão-corrosão"
    }, {
      en: "Lightweight high-strength filter elements for offshore applications",
      zh: "允许减薄壁厚减轻重量，为海洋海上平台高压过滤器提供高强度结构件",
      es: "Elementos filtrantes ligeros de alta resistencia para aplicaciones offshore",
      de: "Leichte, hochfeste Filterelemente für Offshore-Anwendungen",
      fr: "Éléments filtrants allégés à haute résistance pour applications offshore",
      pt: "Elementos filtrantes leves de alta resistência para aplicações offshore"
    }],
    primaryApplications: {
      en: "Offshore oil & gas separation, seawater filtration, pulp & paper digestors",
      zh: "海洋海上平台油气分离装置、原海水取水过滤滤网、造纸蒸煮蒸球洗网",
      es: "Separación marina de petróleo y gas, filtración de agua de mar, digestores de pasta y papel",
      de: "Offshore-Öl- und Gastrennung, Meerwasserfiltration, Zellstoff- und Papierkocher",
      fr: "Séparation pétrolière et gazière offshore, filtration d'eau de mer, digesteurs de pâte à papier",
      pt: "Separação offshore de óleo e gás, filtração de água do mar, digestores de celulose e papel"
    },
    composition: [{
      element: "Cr",
      percentage: "21.0 - 23.0%"
    }, {
      element: "Ni",
      percentage: "4.5 - 6.5%"
    }, {
      element: "Mo",
      percentage: "2.5 - 3.5%"
    }, {
      element: "N",
      percentage: "0.14 - 0.20%"
    }, {
      element: "Fe",
      percentage: "Balance"
    }]
  }]
}, {
  id: "nickel-alloys",
  label: {
    en: "Nickel & Superalloys",
    zh: "镍基与耐蚀超合金",
    es: "Níquel y superaleaciones",
    de: "Nickel- & Superlegierungen",
    fr: "Nickel et superalliages",
    pt: "Níquel e superligas"
  },
  description: {
    en: "Maximum chemical stability in extreme media including hot concentrated acids, caustic alkalis, and severe oxidizing environments.",
    zh: "在极端化工介质中提供最高化学稳定性，包括热浓酸、强碱腐蚀以及剧烈氧化环境。",
    es: "Máxima estabilidad química en medios extremos que incluyen ácidos concentrados calientes, álcalis cáusticos y ambientes fuertemente oxidantes.",
    de: "Maximale chemische Beständigkeit in extremen Medien wie heißen konzentrierten Säuren, Laugen und stark oxidierenden Umgebungen.",
    fr: "Stabilité chimique maximale dans les milieux extrêmes, notamment les acides chauds concentrés, les bases caustiques et les environnements oxydants sévères.",
    pt: "Máxima estabilidade química em meios extremos, incluindo ácidos concentrados a quente, álcalis cáusticos e ambientes fortemente oxidantes."
  },
  alloys: [{
    id: "hastelloy-c276",
    grade: "Hastelloy C-276",
    unsNumber: "N10276",
    enDesignation: "2.4819",
    corrosionRating: "Extreme",
    corrosionRatingI18n: {
      en: "Extreme",
      zh: "极致",
      es: "Extrema",
      de: "Extrem",
      fr: "Extrême",
      pt: "Extrema"
    },
    keyFeatures: [{
      en: "Unrivaled resistance to wet chlorine gas, hypochlorites, and chlorine dioxide",
      zh: "对湿氯气、次氯酸盐溶液及二氧化氯气体具备无可匹敌的耐受力",
      es: "Resistencia inigualable al cloro gas húmedo, hipocloritos y dióxido de cloro",
      de: "Unübertroffene Beständigkeit gegen feuchtes Chlorgas, Hypochlorite und Chlordioxid",
      fr: "Résistance sans égale au chlore gazeux humide, hypochlorites et dioxyde de chlore",
      pt: "Resistência incomparável a gás cloro úmido, hipocloritos e dióxido de cloro"
    }, {
      en: "Tungsten addition suppresses localized corrosion in severe reduce/oxidize chemical mixes",
      zh: "添加钨元素，在还原性与氧化性混合交替的严酷化工液中抑制局部点蚀",
      es: "La adición de tungsteno suprime la corrosión localizada en mezclas químicas reductoras/oxidantes severas",
      de: "Wolframzusatz unterdrückt lokale Korrosion in aggressiven Redox-Gemischen",
      fr: "L'addition de tungstène élimine la corrosion localisée dans les mélanges chimiques redox sévères",
      pt: "A adição de tungstênio inibe a corrosão localizada em misturas químicas redutoras/oxidantes severas"
    }, {
      en: "Virtually immune to pitting, crevice corrosion, and stress corrosion cracking",
      zh: "对点蚀、缝隙腐蚀与应力腐蚀开裂近乎完全免疫",
      es: "Prácticamente inmune a picaduras, corrosión por rendijas y corrosión bajo tensión",
      de: "Praktisch immun gegen Lochfraß, Spaltkorrosion und Spannungsrisskorrosion",
      fr: "Virtuellement insensible aux piqûres, à la corrosion caverneuse et à la corrosion sous contrainte",
      pt: "Praticamente imune a pites, corrosão por frestas e corrosão sob tensão"
    }],
    primaryApplications: {
      en: "Flue gas desulfurization (FGD), sour gas processing, hazardous waste incinerator filters",
      zh: "电厂烟气脱硫喷淋吸收段滤网、高含硫酸性天然气过滤、危废焚烧净化滤芯",
      es: "Desulfuración de gases de combustión (FGD), tratamiento de gas amargo, filtros para incineradores de residuos peligrosos",
      de: "Rauchgasentschwefelung (REA), Sauergasaufbereitung, Filter für Sondermüllverbrennungsanlagen",
      fr: "Désulfuration des fumées (FGD), traitement de gaz acide, filtres d'incinération de déchets dangereux",
      pt: "Dessulfurização de gases de combustão (FGD), tratamento de gás ácido, filtros para incineradores de resíduos perigosos"
    },
    composition: [{
      element: "Ni",
      percentage: "Balance (~57%)"
    }, {
      element: "Mo",
      percentage: "15.0 - 17.0%"
    }, {
      element: "Cr",
      percentage: "14.5 - 16.5%"
    }, {
      element: "Fe",
      percentage: "4.0 - 7.0%"
    }, {
      element: "W",
      percentage: "3.0 - 4.5%"
    }]
  }, {
    id: "inconel-600",
    grade: "Inconel 600",
    unsNumber: "N06600",
    enDesignation: "2.4816",
    corrosionRating: "Extreme",
    corrosionRatingI18n: {
      en: "Extreme",
      zh: "极致",
      es: "Extrema",
      de: "Extrem",
      fr: "Extrême",
      pt: "Extrema"
    },
    keyFeatures: [{
      en: "High nickel content provides immunity to chloride-ion stress-corrosion cracking",
      zh: "超高镍基底成分提供对氯离子应力腐蚀开裂的天然免疫力",
      es: "El alto contenido de níquel proporciona inmunidad al agrietamiento por corrosión bajo tensión por iones cloruro",
      de: "Hoher Nickelgehalt verleiht Immunität gegen chloridinduzierte Spannungsrisskorrosion",
      fr: "Sa forte teneur en nickel confère une immunité totale à la corrosion sous contrainte par ions chlorure",
      pt: "O alto teor de níquel confere imunidade à corrosão sob tensão induzida por cloretos"
    }, {
      en: "Excellent resistance to high-temperature dry chlorine and hydrogen chloride gases",
      zh: "对高温干燥氯气与氯化氢高温腐蚀气体展现卓越的化学惰性",
      es: "Excelente resistencia a gases de cloro seco y cloruro de hidrógeno a alta temperatura",
      de: "Hervorragende Beständigkeit gegen trockenes Chlorgas und Chlorwasserstoff bei hohen Temperaturen",
      fr: "Excellente tenue aux gaz de chlore sec et de chlorure d'hydrogène à haute température",
      pt: "Excelente resistência a gases de cloro seco e cloreto de hidrogênio em altas temperaturas"
    }, {
      en: "Superior oxidation resistance at extreme high temperature service",
      zh: "在极端超高温连续服役状态下保持优异的抗氧化性与抗剥落性",
      es: "Resistencia superior a la oxidación en servicio a temperaturas extremadamente altas",
      de: "Hervorragende Oxidationsbeständigkeit im extremen Hochtemperaturbetrieb",
      fr: "Tenue remarquable à l'oxydation en service continu à très haute température",
      pt: "Resistência superior à oxidação em serviço contínuo a temperaturas extremamente altas"
    }],
    primaryApplications: {
      en: "Nuclear reactor core strainers, furnace atmosphere filtration, chemical thermal cracking",
      zh: "核反应堆一回路精密过滤器、工业高温热处理炉保护气氛过滤、石化高温裂解滤芯",
      es: "Filtros para núcleos de reactores nucleares, filtración de atmósferas de hornos, craqueo térmico químico",
      de: "Kernreaktor-Filtersiebe, Ofenatmosphären-Filtration, chemische thermische Spaltanlagen",
      fr: "Crépines de réacteurs nucléaires, filtration des atmosphères de fours, vapocraquage chimique",
      pt: "Filtros de núcleo para reatores nucleares, filtração de atmosferas de fornos, craqueamento térmico químico"
    },
    composition: [{
      element: "Ni",
      percentage: "≥ 72.0%"
    }, {
      element: "Cr",
      percentage: "14.0 - 17.0%"
    }, {
      element: "Fe",
      percentage: "6.0 - 10.0%"
    }, {
      element: "C",
      percentage: "≤ 0.15%"
    }]
  }, {
    id: "monel-400",
    grade: "Monel 400",
    unsNumber: "N04400",
    enDesignation: "2.4360",
    corrosionRating: "Superior",
    corrosionRatingI18n: {
      en: "Superior",
      zh: "卓越",
      es: "Superior",
      de: "Überlegen",
      fr: "Supérieure",
      pt: "Superior"
    },
    keyFeatures: [{
      en: "Solid-solution alloy offering outstanding resistance to hydrofluoric acid and alkalis",
      zh: "单相固溶体镍铜合金，对氢氟酸介质与各浓度苛性碱液具有非凡抗蚀性",
      es: "Aleación de solución sólida que ofrece excelente resistencia al ácido fluorhídrico y a los álcalis",
      de: "Einphasige Mischkristalllegierung mit herausragender Beständigkeit gegen Flusssäure und Laugen",
      fr: "Alliage à solution solide offrant une tenue exceptionnelle à l'acide fluorhydrique et aux alcalis",
      pt: "Liga de solução sólida com excelente resistência ao ácido fluorídrico e a soluções alcalinas"
    }, {
      en: "Exceptional durability in rapidly flowing seawater and marine biofouling conditions",
      zh: "在高流速湍流海水冲刷及海洋生物附着工况下仍保持出色的使用寿命",
      es: "Excepcional durabilidad en agua de mar de flujo rápido y condiciones de bioincrustación marina",
      de: "Hervorragende Beständigkeit in schnell strömendem Meerwasser und gegen Meeresbewuchs",
      fr: "Durabilité exceptionnelle dans l'eau de mer à fort débit et face au bio-encrassement marin",
      pt: "Excepcional durabilidade em água do mar de fluxo rápido e sob incrustação biológica marinha"
    }, {
      en: "Non-magnetic with high mechanical toughness down to cryogenic temperatures",
      zh: "呈无磁性，且从常温直至深冷超低温区间均保持优良的机械冲击韧性",
      es: "No magnético, con alta tenacidad mecánica hasta temperaturas criogénicas",
      de: "Unmagnetisch mit hoher mechanischer Zähigkeit bis in den Tieftemperaturbereich",
      fr: "Amagnétique, conservant une haute ténacité mécanique jusqu'aux températures cryogéniques",
      pt: "Não magnético, com alta tenacidade mecânica até temperaturas criogênicas"
    }],
    primaryApplications: {
      en: "Alkylation plants, marine filter screens, salt production separation, brine pumps",
      zh: "炼油厂氢氟酸烷基化装置、舰船海水过滤器网篮、制盐离心过滤分离、高浓度盐水泵网",
      es: "Plantas de alquilación, tamices de filtración marina, separación en producción de sal, bombas de salmuera",
      de: "Alkylierungsanlagen, marine Filtersiebe, Trennung in der Salzproduktion, Solepumpen",
      fr: "Unités d'alkylation, tamis marins, séparation dans la production de sel, pompes à saumure",
      pt: "Plantas de alquilação, telas de filtragem marítima, separação na produção de sal, bombas de salmoura"
    },
    composition: [{
      element: "Ni",
      percentage: "63.0% min"
    }, {
      element: "Cu",
      percentage: "28.0 - 34.0%"
    }, {
      element: "Fe",
      percentage: "2.5% max"
    }, {
      element: "Mn",
      percentage: "2.0% max"
    }]
  }]
}, {
  id: "titanium-specialty",
  label: {
    en: "Titanium & Refractory",
    zh: "钛合金与难熔金属",
    es: "Titanio y metales refractarios",
    de: "Titan & Refraktärmetalle",
    fr: "Titane et métaux réfractaires",
    pt: "Titânio e metais refratários"
  },
  description: {
    en: "Ultra-lightweight or high-purity elemental wire mesh for biomedical, electrochemistry, and severe chlorine dioxide bleaching.",
    zh: "高纯度金属精密织网，专用于生物医疗、电化学电极基底及二氧化氯强氧化漂白工序。",
    es: "Malla de alambre ultraligera o de alta pureza para aplicaciones biomédicas, electroquímica y blanqueo con dióxido de cloro severo.",
    de: "Ultraleichtes oder hochreines Drahtgewebe für Biomedizin, Elektrochemie und aggressive Chlordioxid-Bleichprozesse.",
    fr: "Toiles métalliques ultra-légères ou de haute pureté pour le biomédical, l'électrochimie et le blanchiment au dioxyde de chlore.",
    pt: "Malhas de arame ultraleves ou de alta pureza para aplicações biomédicas, eletroquímica e branqueamento severo com dióxido de cloro."
  },
  alloys: [{
    id: "titanium-gr2",
    grade: "Titanium Grade 2",
    unsNumber: "R50400",
    enDesignation: "3.7035",
    corrosionRating: "Extreme",
    corrosionRatingI18n: {
      en: "Extreme",
      zh: "极致",
      es: "Extrema",
      de: "Extrem",
      fr: "Extrême",
      pt: "Extrema"
    },
    keyFeatures: [{
      en: "Spontaneous formation of stable oxide surface coating gives complete immunity to saltwater",
      zh: "表面自发形成致密坚固的钝化氧化钛薄膜，对天然海水及盐水实现完全免疫",
      es: "La formación espontánea de una película de óxido estable otorga total inmunidad al agua salada",
      de: "Spontane Bildung einer dichten Oxidschicht sorgt für vollständige Meerwasserbeständigkeit",
      fr: "Formation spontanée d'un film d'oxyde protecteur garantissant une immunité totale à l'eau de mer",
      pt: "Formação espontânea de película estável de óxido confere imunidade total à água salgada"
    }, {
      en: "High strength-to-weight ratio with non-magnetic properties",
      zh: "卓越的比强度（强度/重量比），重量仅为不锈钢约 60%，兼具无磁特性",
      es: "Alta relación resistencia-peso con propiedades no magnéticas",
      de: "Hohes Festigkeits-Gewichts-Verhältnis mit unmagnetischen Eigenschaften",
      fr: "Rapport résistance/poids élevé et propriétés amagnétiques",
      pt: "Alta relação resistência-peso com propriedades não magnéticas"
    }, {
      en: "Resists nitric acid, wet chlorine, and organic acid solutions",
      zh: "耐受氧化性硝酸、湿氯气介质以及绝大多数工业有机酸水溶液",
      es: "Resiste ácido nítrico, cloro húmedo y soluciones de ácidos orgánicos",
      de: "Beständig gegen Salpetersäure, feuchtes Chlor und organische Säurelösungen",
      fr: "Résiste à l'acide nitrique, au chlore humide et aux solutions d'acides organiques",
      pt: "Resiste a ácido nítrico, cloro úmido e soluções de ácidos orgânicos"
    }],
    primaryApplications: {
      en: "Seawater desalination filter screens, electroplating anodes, titanium catalyst grids",
      zh: "大型海水淡化取水前置过滤筛网、电镀与氯碱工业阳极网篮、钛基催化反应栅网",
      es: "Tamices filtrantes para desalinización de agua de mar, ánodos de galvanoplastia, rejillas catalíticas de titanio",
      de: "Meerwasserentsalzungs-Filtersiebe, Galvanikanoden, Titankatalysatorgitter",
      fr: "Tamis de dessalement d'eau de mer, anodes d'électroplastie, grilles catalytiques en titane",
      pt: "Telas para dessalinização de água do mar, ânodos de galvanoplastia, grades de catalisador em titânio"
    },
    composition: [{
      element: "Ti",
      percentage: "Balance (>99%)"
    }, {
      element: "Fe",
      percentage: "0.30% max"
    }, {
      element: "O",
      percentage: "0.25% max"
    }, {
      element: "C",
      percentage: "0.08% max"
    }]
  }, {
    id: "nickel-200",
    grade: "Pure Nickel 200",
    unsNumber: "N02200",
    enDesignation: "2.4066",
    corrosionRating: "Superior",
    corrosionRatingI18n: {
      en: "Superior",
      zh: "卓越",
      es: "Superior",
      de: "Überlegen",
      fr: "Supérieure",
      pt: "Superior"
    },
    keyFeatures: [{
      en: "Commercially pure wrought nickel with high electrical and thermal conductivity",
      zh: "高纯度工业纯镍，具备优良的导电性、导热性及抗接触阻抗性能",
      es: "Níquel forjado comercialmente puro con alta conductividad eléctrica y térmica",
      de: "Reines Industrienickel mit hoher elektrischer und thermischer Leitfähigkeit",
      fr: "Nickel forgé commercialement pur avec haute conductivité électrique et thermique",
      pt: "Níquel forjado comercialmente puro com alta condutividade elétrica e térmica"
    }, {
      en: "Unsurpassed resistance to caustic alkalis across all concentrations and temperatures",
      zh: "在任何浓度与高温熔融条件下的苛性碱液（烧碱、氢氧化钾）中具备无以伦比的耐蚀性",
      es: "Resistencia insuperable a álcalis cáusticos en todas las concentraciones y temperaturas",
      de: "Unübertroffene Beständigkeit gegen Ätzalkalien über alle Konzentrationen und Temperaturen",
      fr: "Résistance inégalée aux alcalis caustiques à toutes concentrations et températures",
      pt: "Resistência insuperável a álcalis cáusticos em todas as concentrações e temperaturas"
    }, {
      en: "High ductility for ultra-fine precision weave patterns",
      zh: "高塑性与延展性，适宜织造微米级高密度微孔金属平织与荷兰网",
      es: "Alta ductilidad para patrones de tejido de ultra-precisión",
      de: "Hohe Duktilität für ultrafeine Präzisionsgewebestrukturen",
      fr: "Haute ductilité pour des tissages de précision ultra-fins",
      pt: "Alta ductilidade para padrões de tecelagem de ultraprecisão"
    }],
    primaryApplications: {
      en: "Caustic soda manufacturing filters, battery electrode mesh, synthetic fiber spinning",
      zh: "离子膜烧碱生产碱液循环过滤、锂电氢能电池集流体电极网、合成纤维纺丝组件滤芯",
      es: "Filtros en fabricación de sosa cáustica, mallas para electrodos de baterías, hilatura de fibras sintéticas",
      de: "Filter für die Natronlaugeherstellung, Batterie-Elektrodengitter, Spinnfilter für Synthesefasern",
      fr: "Filtres pour fabrication de soude caustique, toiles pour électrodes de batteries, filage de fibres synthétiques",
      pt: "Filtros para fabricação de soda cáustica, malhas para eletrodos de baterias, fiação de fibras sintéticas"
    },
    composition: [{
      element: "Ni",
      percentage: "≥ 99.0%"
    }, {
      element: "Cu",
      percentage: "0.25% max"
    }, {
      element: "Fe",
      percentage: "0.40% max"
    }, {
      element: "Mn",
      percentage: "0.35% max"
    }]
  }]
}];
export default function Alloys() {
  const {
    lang
  } = useLanguage();
  const [activeTab, setActiveTab] = useState("austenitic");
  const [selectedAlloyId, setSelectedAlloyId] = useState("ss-316l");
  const currentCategory = ALLOY_CATEGORIES.find(cat => cat.id === activeTab) || ALLOY_CATEGORIES[0];
  const selectedAlloy = currentCategory.alloys.find(a => a.id === selectedAlloyId) || currentCategory.alloys[0];
  const getBadgeVariant = (rating: AlloySpec["corrosionRating"]) => {
    switch (rating) {
      case "Extreme":
        return "default";
      case "Superior":
        return "secondary";
      case "High":
        return "outline";
      default:
        return "outline";
    }
  };
  const copy = {
    badge: {
      en: "Metallurgical Specifications",
      zh: "冶金材料规范",
      es: "Especificaciones metalúrgicas",
      de: "Metallurgische Spezifikationen",
      fr: "Spécifications métallurgiques",
      pt: "Especificações metalúrgicas"
    },
    h2: {
      en: "High-Performance Alloys & Raw Materials",
      zh: "高性能合金材料与优质母料",
      es: "Aleaciones de alto rendimiento y materias primas",
      de: "Hochleistungslegierungen & Rohmaterialien",
      fr: "Alliages haute performance et matières premières",
      pt: "Ligas de alto desempenho e matérias-primas"
    },
    lead: {
      en: "MutualFilter precision-weaves woven wire mesh, filter screens, and multi-layer sintered media from certified, mill-test-verified corrosion-resistant and high-temperature alloys.",
      zh: "MutualFilter 选用经过钢厂化验单严格验证的耐腐蚀与耐高温正材合金丝线，精密编织高品质金属丝网、滤芯组件及多层真空烧结滤材。",
      es: "MutualFilter teje con precisión mallas de alambre, tamices filtrantes y medios sinterizados multicapa a partir de aleaciones resistentes a la corrosión y altas temperaturas con certificación de acería.",
      de: "MutualFilter webt hochpräzise Drahtgewebe, Filtersiebe und mehrlagige Sintermedien aus werksgeprüften, korrosions- und hochtemperaturbeständigen Qualitätslegierungen.",
      fr: "MutualFilter tisse avec précision des toiles métalliques, des tamis filtrants et des médias frittés multicouches à partir d'alliages certifiés d'aciérie, résistants à la corrosion et aux hautes températures.",
      pt: "A MutualFilter tece com precisão malhas de arame, telas filtrantes e meios sinterizados multicamadas a partir de ligas certificadas de usina, resistentes à corrosão e a altas temperaturas."
    },
    compatibilityGuideBtn: {
      en: "Compatibility Guide",
      zh: "材料兼容性指南",
      es: "Guía de compatibilidad",
      de: "Kompatibilitätsleitfaden",
      fr: "Guide de compatibilité",
      pt: "Guia de compatibilidade"
    },
    selectGradeHint: {
      en: "Select Grade for Details",
      zh: "点击选择合金牌号查看详情",
      es: "Seleccione un grado para ver detalles",
      de: "Sorte für Details auswählen",
      fr: "Sélectionnez une nuance pour les détails",
      pt: "Selecione o grau para detalhes"
    },
    corrosionSuffix: {
      en: "Corrosion",
      zh: "耐腐蚀评级",
      es: "Corrosión",
      de: "Korrosion",
      fr: "Corrosion",
      pt: "Corrosão"
    },
    enStandardPrefix: {
      en: "EN / DIN Standard:",
      zh: "EN / DIN 欧洲标准:",
      es: "Norma EN / DIN:",
      de: "EN / DIN-Norm:",
      fr: "Norme EN / DIN :",
      pt: "Norma EN / DIN:"
    },
    nominalCompHeading: {
      en: "Nominal Chemical Composition",
      zh: "标称化学成分配比",
      es: "Composición química nominal",
      de: "Chemische Richtanalyse",
      fr: "Composition chimique nominale",
      pt: "Composição química nominal"
    },
    performanceAdvHeading: {
      en: "Performance Advantages",
      zh: "核心性能与冶金优势",
      es: "Ventajas de rendimiento",
      de: "Leistungsvorteile",
      fr: "Avantages de performance",
      pt: "Vantagens de desempenho"
    },
    targetAppHeading: {
      en: "Target Filtration Applications",
      zh: "重点工程过滤应用领域",
      es: "Aplicaciones de filtración objetivo",
      de: "Ziel-Filtrationsanwendungen",
      fr: "Applications de filtration cibles",
      pt: "Aplicações de filtração alvo"
    },
    requestCustomQuote: {
      en: "Request Custom Alloy Quote",
      zh: "索取特种合金报价",
      es: "Solicitar presupuesto de aleación a medida",
      de: "Angebot für Sonderlegierung anfragen",
      fr: "Demander un devis pour alliage sur mesure",
      pt: "Solicitar orçamento de liga sob medida"
    },
    downloadDatasheet: {
      en: "Download Material Datasheet",
      zh: "下载材料技术规格书",
      es: "Descargar ficha técnica del material",
      de: "Material-Datenblatt herunterladen",
      fr: "Télécharger la fiche technique matière",
      pt: "Baixar ficha técnica do material"
    },
    matrixHeading: {
      en: "Alloy Quick Comparison Matrix",
      zh: "核心合金性能速查矩阵",
      es: "Matriz de comparación rápida de aleaciones",
      de: "Schnellvergleichsmatrix der Legierungen",
      fr: "Matrice de comparaison rapide des alliages",
      pt: "Matriz de comparação rápida de ligas"
    },
    matrixSubtitle: {
      en: "Cross-reference wire mesh material properties for high-temperature and chemical filtration suitability.",
      zh: "交叉比对各合金丝网耐温极限与耐化学酸碱特性，快速确定最适材质。",
      es: "Cruce las propiedades de los materiales de malla para verificar su idoneidad en altas temperaturas y filtración química.",
      de: "Vergleichen Sie Gewebewerkstoffe auf Eignung für Hochtemperatur- und chemische Filtrationsanwendungen.",
      fr: "Croisez les propriétés des toiles métalliques pour vérifier leur adéquation à haute température et en filtration chimique.",
      pt: "Cruze as propriedades dos materiais de malha para avaliar a adequação a altas temperaturas e filtração química."
    },
    viewFullChartBtn: {
      en: "View Full Corrosion Chart",
      zh: "查阅完整耐腐蚀图表",
      es: "Ver tabla completa de corrosión",
      de: "Vollständige Korrosionstabelle anzeigen",
      fr: "Voir le tableau complet de corrosion",
      pt: "Ver tabela completa de corrosão"
    },
    thAlloyGrade: {
      en: "Alloy Grade",
      zh: "合金牌号",
      es: "Grado de aleación",
      de: "Legierungsgüte",
      fr: "Nuance d'alliage",
      pt: "Grau de liga"
    },
    thUnsEn: {
      en: "UNS / EN",
      zh: "UNS / EN 标准号",
      es: "UNS / EN",
      de: "UNS / EN",
      fr: "UNS / EN",
      pt: "UNS / EN"
    },
    thChlorideResistance: {
      en: "Chloride Resistance",
      zh: "抗氯离子腐蚀",
      es: "Resistencia a cloruros",
      de: "Chloridbeständigkeit",
      fr: "Résistance aux chlorures",
      pt: "Resistência a cloretos"
    },
    thAcidResistance: {
      en: "Acid Resistance",
      zh: "耐酸介质能力",
      es: "Resistencia a ácidos",
      de: "Säurebeständigkeit",
      fr: "Résistance aux acides",
      pt: "Resistência a ácidos"
    },
    thVerification: {
      en: "Verification",
      zh: "正材检验能力",
      es: "Verificación",
      de: "Prüfung",
      fr: "Vérification",
      pt: "Verificação"
    },
    moderate: {
      en: "Moderate",
      zh: "中等",
      es: "Moderada",
      de: "Mäßig",
      fr: "Modérée",
      pt: "Moderada"
    },
    organicMild: {
      en: "Organic / Mild",
      zh: "有机酸 / 弱酸",
      es: "Orgánicos / leves",
      de: "Organisch / mild",
      fr: "Organiques / doux",
      pt: "Orgânicos / brandos"
    },
    pmiTestable: {
      en: "PMI Testable",
      zh: "支持 PMI 光谱分析",
      es: "Ensayable por PMI",
      de: "PMI-prüfbar",
      fr: "Contrôlable PMI",
      pt: "Testável por PMI"
    },
    highVal: {
      en: "High",
      zh: "优良",
      es: "Alta",
      de: "Hoch",
      fr: "Élevée",
      pt: "Alta"
    },
    moderateAcids: {
      en: "Moderate Acids",
      zh: "中等浓度酸液",
      es: "Ácidos moderados",
      de: "Mäßige Säuren",
      fr: "Acides modérés",
      pt: "Ácidos moderados"
    },
    superiorVal: {
      en: "Superior",
      zh: "卓越",
      es: "Superior",
      de: "Überlegen",
      fr: "Supérieure",
      pt: "Superior"
    },
    sulfuricPhosphoric: {
      en: "Sulfuric / Phosphoric",
      zh: "浓硫酸 / 磷酸",
      es: "Sulfúrico / fosfórico",
      de: "Schwefel- / Phosphorsäure",
      fr: "Sulfurique / phosphorique",
      pt: "Sulfúrico / fosfórico"
    },
    extremeVal: {
      en: "Extreme",
      zh: "极端耐受",
      es: "Extrema",
      de: "Extrem",
      fr: "Extrême",
      pt: "Extrema"
    },
    concentratedHydrochloric: {
      en: "Concentrated Hydrochloric",
      zh: "强浓盐酸 / 湿氯气",
      es: "Clorhídrico concentrado",
      de: "Konzentrierte Salzsäure",
      fr: "Chlorhydrique concentré",
      pt: "Clorídrico concentrado"
    },
    millTestCertified: {
      en: "Mill Test Certified",
      zh: "钢厂化验报告认证",
      es: "Certificado de acería",
      de: "Werkszeugnis zertifiziert",
      fr: "Certifié par essai usine",
      pt: "Certificado de usina"
    },
    immuneSeawater: {
      en: "Immune (Seawater)",
      zh: "完全免疫 (海水环境)",
      es: "Inmune (agua de mar)",
      de: "Immun (Meerwasser)",
      fr: "Immunisé (eau de mer)",
      pt: "Imune (água do mar)"
    },
    wetChlorineNitric: {
      en: "Wet Chlorine / Nitric",
      zh: "湿氯气 / 硝酸",
      es: "Cloro húmedo / nítrico",
      de: "Feuchtes Chlor / Salpetersäure",
      fr: "Chlore humide / nitrique",
      pt: "Cloro úmido / nítrico"
    },
    footNote: {
      en: "MutualFilter mesh materials are available with EN 10204 3.1 inspection certificates and Positive Material Identification (PMI) testing options when specified in the order.",
      zh: "MutualFilter 金属网材料在订单明确要求时，可提供 EN 10204 3.1 材质检验报告与 PMI 正材光谱分析服务。",
      es: "Los materiales de malla de MutualFilter están disponibles con certificados de inspección EN 10204 3.1 y ensayos PMI cuando se especifiquen en el pedido.",
      de: "MutualFilter-Gewebewerkstoffe sind auf Bestellung mit Abnahmeprüfzeugnissen nach EN 10204 3.1 und PMI-Materialprüfung lieferbar.",
      fr: "Les toiles métalliques MutualFilter sont livrables avec certificats EN 10204 3.1 et options de contrôle PMI lorsque spécifiés à la commande.",
      pt: "Os materiais de malha da MutualFilter estão disponíveis com certificados de inspeção EN 10204 3.1 e testes PMI quando especificados no pedido."
    },
    materialVerificationLink: {
      en: "Material Verification Process",
      zh: "材质化验认证流程",
      es: "Proceso de verificación de materiales",
      de: "Materialprüfungsverfahren",
      fr: "Processus de vérification des matériaux",
      pt: "Processo de verificação de materiais"
    },
    surfaceSamplesAlt: {
      en: "Stainless steel surface finish samples comparison showing pickled and passivated corrosion protection swatches",
      zh: "不锈钢金属丝网表面处理对比样板：酸洗与钝化耐腐蚀防护试样",
      es: "Muestras de acabado superficial de acero inoxidable comparando decapado y pasivado",
      de: "Edelstahl-Oberflächenbehandlungsmuster im Vergleich: gebeizte und passivierte Proben",
      fr: "Échantillons d'états de surface en acier inoxydable comparant le décapage et la passivation",
      pt: "Amostras de acabamento de superfície em aço inoxidável comparando decapagem e passivação"
    },
    surfaceSamplesCaption: {
      en: "Chemical surface treatments: ultrasonic degreasing, acid pickling, and ASTM A967 passivation ensure optimal chromium oxide layer formation.",
      zh: "化学表面处理：超声波脱脂除油、酸洗及符合 ASTM A967 的钝化工艺，确保表面致密氧化铬保护钝化膜的充分生成。",
      es: "Tratamientos superficiales químicos: desengrase por ultrasonidos, decapado ácido y pasivado según ASTM A967.",
      de: "Chemische Oberflächenbehandlung: Ultraschallentfettung, Säurebeizen und Passivierung nach ASTM A967.",
      fr: "Traitements de surface chimiques : dégraissage ultrasonique, décapage acide et passivation selon ASTM A967.",
      pt: "Tratamentos químicos de superfície: desengraxe ultrassônico, decapagem ácida e passivação conforme ASTM A967."
    }
  };
  return <section id="alloys" data-nav="light" className="relative bg-background text-foreground mf-section overflow-hidden border-b border-border">
      {/* Background Subtle Industrial Texture Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.07] bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />

      <div className="mf-container relative z-10">
        {/* Section Header */}
        <Reveal className="max-w-3xl mx-auto text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border mf-caption font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            <Microscope className="size-3.5 text-primary" />
            <span>{pick(lang, copy.badge)}</span>
          </div>

          <h2 className="mf-h2 text-foreground font-default mb-4">
            {pick(lang, copy.h2)}
          </h2>

          <p className="mf-lead text-muted-foreground leading-relaxed">
            {pick(lang, copy.lead)}
          </p>
        </Reveal>

        {/* Interactive Alloys Section */}
        <Reveal delay={60} className="space-y-8">
          <Tabs defaultValue="austenitic" value={activeTab} onValueChange={val => {
          setActiveTab(val);
          const cat = ALLOY_CATEGORIES.find(c => c.id === val);
          if (cat && cat.alloys.length > 0) {
            setSelectedAlloyId(cat.alloys[0].id);
          }
        }} className="w-full">
            {/* Category Tabs Header */}
            <div className="flex justify-center mb-8">
              <TabsList className="flex flex-wrap h-auto gap-1.5 p-1.5 bg-muted/80 rounded-lg border border-border max-w-full justify-center">
                {ALLOY_CATEGORIES.map((cat, idx) => <TabsTrigger key={cat.id} value={cat.id} data-index={idx} className="px-4 py-2 text-sm font-medium rounded-lg transition-all data-[state=active]:bg-card data-[state=active]:text-foreground">
                    {pick(lang, cat.label)}
                  </TabsTrigger>)}
              </TabsList>
            </div>

            {/* Tab Contents */}
            {ALLOY_CATEGORIES.map(cat => <TabsContent key={cat.id} value={cat.id} className="mt-0 outline-none">
                <div className="bg-card text-card-foreground rounded-lg border border-border overflow-hidden">
                  {/* Category Banner */}
                  <div className="bg-muted/40 border-b border-border p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="mf-h3 text-foreground">
                        {pick(lang, cat.label)}
                      </h3>
                      <p className="mf-caption text-muted-foreground mt-1 max-w-2xl">
                        {pick(lang, cat.description)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 self-start md:self-auto shrink-0">
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/MaterialCompatibilityGuidePage">
                          <ShieldCheck className="size-4 mr-1.5 text-primary" />
                          <span>{pick(lang, copy.compatibilityGuideBtn)}</span>
                        </Link>
                      </Button>
                    </div>
                  </div>

                  {/* Alloys Grid Layout: List + Detail Inspector */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-border">
                    {/* Alloy Selector Column */}
                    <div className="lg:col-span-5 p-6 space-y-4 bg-background/50">
                      <span className="mf-caption font-semibold text-muted-foreground uppercase tracking-wider block mb-2">
                        {pick(lang, copy.selectGradeHint)}
                      </span>

                      <div className="space-y-3">
                        {cat.alloys.map((alloy, i) => {
                      const isSelected = alloy.id === selectedAlloy.id;
                      return <div key={alloy.id} data-index={i} onClick={() => setSelectedAlloyId(alloy.id)} className={`cursor-pointer rounded-lg p-4 border mf-hover-lift transition-all ${isSelected ? "bg-card border-primary ring-1 ring-primary/30" : "bg-card/60 hover:bg-card border-border hover:border-muted-foreground/30"}`}>
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="font-bold text-foreground text-base">
                                  {alloy.grade}
                                </span>
                                <Badge variant={getBadgeVariant(alloy.corrosionRating)}>
                                  {pick(lang, alloy.corrosionRatingI18n)} {pick(lang, copy.corrosionSuffix)}
                                </Badge>
                              </div>

                              <div className="flex items-center gap-4 mf-caption text-muted-foreground mb-2">
                                <span>UNS {alloy.unsNumber}</span>
                                <span>•</span>
                                <span>EN {alloy.enDesignation}</span>
                              </div>

                              <p className="mf-caption text-muted-foreground line-clamp-2">
                                {pick(lang, alloy.keyFeatures[0])}
                              </p>
                            </div>;
                    })}
                      </div>
                    </div>

                    {/* Alloy Detail & Composition View Column */}
                    <div className="lg:col-span-7 p-6 sm:p-8 space-y-6 bg-card">
                      <div className="flex flex-wrap items-start justify-between gap-4 pb-4 border-b border-border">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="mf-h3 text-foreground">
                              {selectedAlloy.grade}
                            </h4>
                            <span className="text-sm font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded border border-border">
                              UNS {selectedAlloy.unsNumber}
                            </span>
                          </div>
                          <p className="mf-caption text-muted-foreground mt-1">
                            {pick(lang, copy.enStandardPrefix)} {selectedAlloy.enDesignation}
                          </p>
                        </div>
                      </div>

                      {/* Chemical Composition Grid */}
                      <div>
                        <h5 className="mf-caption font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
                          <Layers className="size-3.5 text-primary" />
                          {pick(lang, copy.nominalCompHeading)}
                        </h5>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {selectedAlloy.composition.map((comp, idx) => <div key={idx} data-index={idx} className="bg-muted/30 border border-border rounded-lg p-2.5 text-center">
                              <span className="mf-caption text-muted-foreground block font-mono">
                                {comp.element}
                              </span>
                              <span className="text-sm font-semibold text-foreground font-mono">
                                {comp.percentage}
                              </span>
                            </div>)}
                        </div>
                      </div>

                      {/* Key Engineering Features */}
                      <div>
                        <h5 className="mf-caption font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
                          <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                          <span>{pick(lang, copy.performanceAdvHeading)}</span>
                        </h5>
                        <ul className="space-y-2">
                          {selectedAlloy.keyFeatures.map((feature, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2 text-sm text-foreground">
                              <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                              <span>{pick(lang, feature)}</span>
                            </li>)}
                        </ul>
                      </div>

                      {/* Primary Application Environments */}
                      <div className="bg-muted/40 border border-border rounded-lg p-4">
                        <span className="mf-caption font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                          {pick(lang, copy.targetAppHeading)}
                        </span>
                        <p className="text-sm text-foreground font-medium">
                          {pick(lang, selectedAlloy.primaryApplications)}
                        </p>
                      </div>

                      {/* Action Links */}
                      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                        <Button variant="default" size="sm" asChild>
                          <Link to="/RequestAQuotePage">
                            <span>{pick(lang, copy.requestCustomQuote)}</span>
                            <ArrowRight className="size-4 ml-1" />
                          </Link>
                        </Button>

                        <Button variant="ghost" size="sm" asChild>
                          <Link to="/DatasheetsPage">
                            <FileText className="size-4 mr-1 text-muted-foreground" />
                            <span>{pick(lang, copy.downloadDatasheet)}</span>
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </Reveal>

        {/* Supporting Image Card: Passivation & Pickling Surface Finishes */}
        <Reveal delay={120} className="mt-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 bg-card border border-border rounded-lg p-6 items-center">
            <div className="mf-media-framed md:col-span-5 mf-ratio-card">
              <img src={siteImage("60e9ef-surface-finish-passiv-85dd3a.webp")} alt={pick(lang, copy.surfaceSamplesAlt)} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300" />
            </div>
            <div className="md:col-span-7 space-y-3">
              <Badge variant="outline" className="text-primary border-primary/30 mf-caption">
                ASTM A967 / ASTM A380
              </Badge>
              <h3 className="mf-h3 text-foreground">
                {pick(lang, {
                en: "Surface Treatments: Passivation & Ultrasonic Cleaning",
                zh: "表面工程处理：酸洗钝化与超声波除油洁净工艺",
                es: "Tratamientos superficiales: Pasivado y limpieza ultrasónica",
                de: "Oberflächenbehandlungen: Passivierung & Ultraschallreinigung",
                fr: "Traitements de surface : Passivation et nettoyage ultrasonique",
                pt: "Tratamentos de superfície: Passivação e limpeza ultrassônica"
              })}
              </h3>
              <p className="mf-caption text-muted-foreground leading-relaxed">
                {pick(lang, copy.surfaceSamplesCaption)}
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <span className="inline-flex items-center gap-1.5 mf-caption font-medium bg-muted text-muted-foreground px-2.5 py-1 rounded-md">
                  <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                  <span>
                    {pick(lang, {
                    en: "Zero free-iron residue",
                    zh: "表面零游离铁残留",
                    es: "Cero residuo de hierro libre",
                    de: "Keine freien Eisenrückstände",
                    fr: "Zéro résidu de fer libre",
                    pt: "Zero resíduo de ferro livre"
                  })}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 mf-caption font-medium bg-muted text-muted-foreground px-2.5 py-1 rounded-md">
                  <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                  <span>
                    {pick(lang, {
                    en: "ASTM A967 Practice E verified",
                    zh: "ASTM A967 E 标准验证",
                    es: "Verificado según ASTM A967 Práctica E",
                    de: "Nach ASTM A967 Praxis E geprüft",
                    fr: "Vérifié selon ASTM A967 Pratique E",
                    pt: "Verificado conforme ASTM A967 Prática E"
                  })}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Global Alloys Comparison Table */}
        <Reveal delay={180} className="mt-16 bg-card border border-border rounded-lg p-6 sm:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="mf-h3 text-foreground">
                {pick(lang, copy.matrixHeading)}
              </h3>
              <p className="mf-caption text-muted-foreground">
                {pick(lang, copy.matrixSubtitle)}
              </p>
            </div>

            <Button variant="outline" size="sm" asChild>
              <Link to="/CorrosionResistanceMatricesPage">
                <span>{pick(lang, copy.viewFullChartBtn)}</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          <div className="overflow-x-auto">
            <Table className="min-w-[720px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent bg-muted/50">
                  <TableHead className="font-bold text-foreground py-3 px-4">{pick(lang, copy.thAlloyGrade)}</TableHead>
                  <TableHead className="font-bold text-foreground py-3 px-4">{pick(lang, copy.thUnsEn)}</TableHead>
                  <TableHead className="font-bold text-foreground py-3 px-4">{pick(lang, copy.thChlorideResistance)}</TableHead>
                  <TableHead className="font-bold text-foreground py-3 px-4">{pick(lang, copy.thAcidResistance)}</TableHead>
                  <TableHead className="font-bold text-foreground py-3 px-4 text-right">{pick(lang, copy.thVerification)}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="hover:bg-muted/40">
                  <TableCell className="font-medium text-foreground py-3 px-4">AISI 304L</TableCell>
                  <TableCell className="text-muted-foreground mf-caption font-mono py-3 px-4">S30403 / 1.4307</TableCell>
                  <TableCell className="text-muted-foreground py-3 px-4">{pick(lang, copy.moderate)}</TableCell>
                  <TableCell className="text-muted-foreground py-3 px-4">{pick(lang, copy.organicMild)}</TableCell>
                  <TableCell className="text-right py-3 px-4">
                    <Badge variant="outline" className="mf-caption">{pick(lang, copy.pmiTestable)}</Badge>
                  </TableCell>
                </TableRow>

                <TableRow className="hover:bg-muted/40">
                  <TableCell className="font-medium text-foreground py-3 px-4">AISI 316L</TableCell>
                  <TableCell className="text-muted-foreground mf-caption font-mono py-3 px-4">S31603 / 1.4404</TableCell>
                  <TableCell className="text-muted-foreground py-3 px-4">{pick(lang, copy.highVal)}</TableCell>
                  <TableCell className="text-muted-foreground py-3 px-4">{pick(lang, copy.moderateAcids)}</TableCell>
                  <TableCell className="text-right py-3 px-4">
                    <Badge variant="outline" className="mf-caption">EN 10204 3.1</Badge>
                  </TableCell>
                </TableRow>

                <TableRow className="hover:bg-muted/40">
                  <TableCell className="font-medium text-foreground py-3 px-4">AISI 904L</TableCell>
                  <TableCell className="text-muted-foreground mf-caption font-mono py-3 px-4">N08904 / 1.4539</TableCell>
                  <TableCell className="text-muted-foreground py-3 px-4">{pick(lang, copy.superiorVal)}</TableCell>
                  <TableCell className="text-muted-foreground py-3 px-4">{pick(lang, copy.sulfuricPhosphoric)}</TableCell>
                  <TableCell className="text-right py-3 px-4">
                    <Badge variant="outline" className="mf-caption">EN 10204 3.1</Badge>
                  </TableCell>
                </TableRow>

                <TableRow className="hover:bg-muted/40">
                  <TableCell className="font-medium text-foreground py-3 px-4">Hastelloy C-276</TableCell>
                  <TableCell className="text-muted-foreground mf-caption font-mono py-3 px-4">N10276 / 2.4819</TableCell>
                  <TableCell className="text-muted-foreground py-3 px-4">{pick(lang, copy.extremeVal)}</TableCell>
                  <TableCell className="text-muted-foreground py-3 px-4">{pick(lang, copy.concentratedHydrochloric)}</TableCell>
                  <TableCell className="text-right py-3 px-4">
                    <Badge variant="default" className="mf-caption">{pick(lang, copy.millTestCertified)}</Badge>
                  </TableCell>
                </TableRow>

                <TableRow className="hover:bg-muted/40">
                  <TableCell className="font-medium text-foreground py-3 px-4">Titanium Gr 2</TableCell>
                  <TableCell className="text-muted-foreground mf-caption font-mono py-3 px-4">R50400 / 3.7035</TableCell>
                  <TableCell className="text-muted-foreground py-3 px-4">{pick(lang, copy.immuneSeawater)}</TableCell>
                  <TableCell className="text-muted-foreground py-3 px-4">{pick(lang, copy.wetChlorineNitric)}</TableCell>
                  <TableCell className="text-right py-3 px-4">
                    <Badge variant="default" className="mf-caption">{pick(lang, copy.millTestCertified)}</Badge>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Quality & Traceability Callout Footer */}
          <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 mf-caption text-muted-foreground">
            <div className="flex items-center gap-2">
              <Info className="size-4 text-primary shrink-0" />
              <span>
                {pick(lang, copy.footNote)}
              </span>
            </div>

            <Link to="/StainlessSteelMaterialVerificationPage" className="text-primary hover:underline font-semibold whitespace-nowrap inline-flex items-center gap-1">
              <span>{pick(lang, copy.materialVerificationLink)}</span>
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>;
}